import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import type { Context, ReactElement, ReactNode } from "react";
import { createContext, createElement, useCallback, useContext, useMemo } from "react";
import {
  HttpFirstCredentialClient,
  type FirstCredentialClient,
  type FirstCredentialCompleteRequest,
  type FirstCredentialCompleteResult,
  type FirstCredentialValidateResult,
} from "./first-credential-client";

/**
 * Client context and mutation hooks for the CLMS-137 first-credential flow
 * (T023). The /set-password operations run as mutations so the fc1
 * authorization and the password only ever live in in-memory mutation state
 * — never in a query key or persistable query cache — and the mutation keys
 * themselves are static, password-free scope labels. The registration-page
 * status/redemption follow-up deliberately calls the client directly with
 * component-local state instead: the registration surface's security
 * contract keeps its TanStack mutation cache empty at terminal states, so
 * the proof and the minted fc1 never enter any cache at all.
 */

const FirstCredentialClientContext: Context<FirstCredentialClient | undefined> = createContext<
  FirstCredentialClient | undefined
>(undefined);

let defaultClient: FirstCredentialClient | undefined;

function getDefaultFirstCredentialClient(): FirstCredentialClient {
  defaultClient ??= new HttpFirstCredentialClient();
  return defaultClient;
}

export interface FirstCredentialClientProviderProps {
  children: ReactNode;
  client?: FirstCredentialClient;
}

export function FirstCredentialClientProvider({
  children,
  client,
}: FirstCredentialClientProviderProps): ReactElement {
  const value = useMemo(() => client ?? getDefaultFirstCredentialClient(), [client]);
  return createElement(FirstCredentialClientContext.Provider, { value }, children);
}

export function useFirstCredentialClient(): FirstCredentialClient {
  return useContext(FirstCredentialClientContext) ?? getDefaultFirstCredentialClient();
}

/** Shared mutationKey scope so the feature's mutations can be purged from the cache. */
const FIRST_CREDENTIAL_MUTATION_SCOPE = ["first-credential"] as const;

export interface FirstCredentialValidationVariables {
  authorization: string;
  /** Aborted by the page at pagehide/unmount (#141). Not sensitive. */
  signal?: AbortSignal;
}

export type FirstCredentialValidationMutation = UseMutationResult<
  FirstCredentialValidateResult,
  Error,
  FirstCredentialValidationVariables
>;

/** Non-consuming readiness check for the claimed fc1. Never retried automatically. */
export function useFirstCredentialValidation(): FirstCredentialValidationMutation {
  const client = useFirstCredentialClient();

  return useMutation({
    mutationKey: [...FIRST_CREDENTIAL_MUTATION_SCOPE, "validate"],
    mutationFn: ({ authorization, signal }: FirstCredentialValidationVariables) =>
      signal ? client.validate({ authorization }, { signal }) : client.validate({ authorization }),
  });
}

export interface FirstCredentialCompletionVariables {
  request: FirstCredentialCompleteRequest;
  idempotencyKey: string;
  /** Aborted by the page at pagehide/unmount (#141). Not sensitive. */
  signal?: AbortSignal;
}

export type FirstCredentialCompletionMutation = UseMutationResult<
  FirstCredentialCompleteResult,
  Error,
  FirstCredentialCompletionVariables
>;

/** Submits one logical completion attempt (first-write-wins). Never retried automatically. */
export function useFirstCredentialCompletion(): FirstCredentialCompletionMutation {
  const client = useFirstCredentialClient();

  return useMutation({
    mutationKey: [...FIRST_CREDENTIAL_MUTATION_SCOPE, "complete"],
    mutationFn: ({ request, idempotencyKey, signal }: FirstCredentialCompletionVariables) =>
      signal
        ? client.complete(request, idempotencyKey, { signal })
        : client.complete(request, idempotencyKey),
  });
}

/**
 * Immediately evicts this feature's mutations from the TanStack mutation
 * cache. Observer `reset()` alone only schedules garbage collection after
 * `gcTime`, which would leave the registration proof (status/redeem
 * variables), the fc1 authorization (redeem data / validate-complete
 * variables), and the password (complete variables) in cache memory after
 * ownership transfers or the flow ends — the UI contract requires clearing
 * sensitive state at those points.
 */
export function useFirstCredentialCachePurge(): () => void {
  const queryClient = useQueryClient();

  return useCallback((): void => {
    const mutationCache = queryClient.getMutationCache();
    for (const mutation of mutationCache.findAll({
      mutationKey: [...FIRST_CREDENTIAL_MUTATION_SCOPE],
    })) {
      mutationCache.remove(mutation);
    }
  }, [queryClient]);
}

/**
 * Completion-only variant of the purge for the recoverable
 * validation_failed path: the completion mutation's variables hold the fc1
 * authorization AND the rejected password, which must not linger in cache
 * memory until gcTime — but the validation mutation (whose `ready` result
 * carries the server password policy the live form still renders) survives.
 */
export function useFirstCredentialCompletionPurge(): () => void {
  const queryClient = useQueryClient();

  return useCallback((): void => {
    const mutationCache = queryClient.getMutationCache();
    for (const mutation of mutationCache.findAll({
      mutationKey: [...FIRST_CREDENTIAL_MUTATION_SCOPE, "complete"],
    })) {
      mutationCache.remove(mutation);
    }
  }, [queryClient]);
}
